import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { tenantHref } from "@/lib/tenantPath";
import { useRequireAuth } from "@/components/auth/useRequireAuth";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
// Relative on purpose: pure modules and components, resolvable under Jest without a shim.
import LoadFailure from "../../components/ui/LoadFailure";
import useMemberRead from "../../pathways/useMemberRead";
import { completeStep, fetchStepScreen, submitPractice, submitQuiz } from "../../pathways/learnerApi";
import { pathwayPath, stepPath } from "../../pathways/learnerModel";
import { pathwayErrorCode } from "../../pathways/errors";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";
import PathwayNotFound from "./PathwayNotFound";
import StepScreenView from "./StepScreenView";

const Spinner = () => (
  <div className="flex justify-center py-16">
    <Loader2 className="animate-spin text-eh-sage-deep" size={28} aria-hidden="true" />
  </div>
);

/**
 * One step's screen for a signed-in learner: `GET /client/pathways/{id}/steps/{sid}`,
 * rendered by StepScreenView.
 *
 *   loading        spinner
 *   404            "not found" (absent pathway or step, not joined)
 *   409 STEP_LOCKED  "This step is not open yet", with the way back to the home
 *   other failure  load failure with retry
 *
 * WRITES. `onComplete` POSTs `/complete`, `onSubmitQuiz` POSTs `/quiz-submit`,
 * `onSubmitPractice` POSTs the multipart `/submission` (contract §10.3); each then
 * re-reads the screen, which is what turns the step "completed" and may open the next one,
 * and announces a finished level or pathway. Their rejections reach the view, which words
 * them.
 */
function StepScreen({ pathwayId, stepId }) {
  const t = useT("public");
  const navigate = useNavigate();
  const fetcher = useCallback(() => fetchStepScreen(pathwayId, stepId), [pathwayId, stepId]);
  const { data, loading, error, notFound, reload } = useMemberRead(fetcher);

  const announce = useCallback((result) => {
    if (result.pathway_completed) toast.success(t("pathways.step.pathwayCompleted"));
    else if (result.level_completed) toast.success(t("pathways.step.levelCompleted"));
  }, [t]);

  const onComplete = useCallback(async () => {
    const result = await completeStep(pathwayId, stepId);
    announce(result);
    reload();
    return result;
  }, [pathwayId, stepId, announce, reload]);

  const onSubmitQuiz = useCallback(async (answers) => {
    const result = await submitQuiz(pathwayId, stepId, answers);
    announce(result);
    reload();
    return result;
  }, [pathwayId, stepId, announce, reload]);

  const onSubmitPractice = useCallback(async (body) => {
    const result = await submitPractice(pathwayId, stepId, body);
    announce(result);
    reload();
    return result;
  }, [pathwayId, stepId, announce, reload]);

  const home = () => navigate(tenantHref(pathwayPath(pathwayId)));

  if (loading && !data) return <Spinner />;
  if (notFound) return <PathwayNotFound />;
  if (pathwayErrorCode(error) === "STEP_LOCKED") {
    return (
      <div data-testid={IDS.stepLocked} className="mx-auto max-w-xl rounded-3xl border border-eh-border-soft bg-eh-cream px-6 py-12 text-center">
        <Lock className="mx-auto h-8 w-8 text-eh-muted" aria-hidden="true" />
        <h1 className="mt-3 font-display text-3xl text-eh-ink">{t("pathways.step.lockedTitle")}</h1>
        <p className="mt-3 text-eh-muted">{t("pathways.step.lockedBody")}</p>
        <button
          type="button"
          onClick={home}
          className="mt-6 inline-flex h-11 items-center rounded-full bg-eh-ink px-6 text-sm font-medium text-eh-cream"
        >
          {t("pathways.step.backHome")}
        </button>
      </div>
    );
  }
  if (error || !data) {
    return (
      <LoadFailure
        message={t("pathways.step.loadFailed")}
        onRetry={reload}
        retryLabel={t("pathways.retry")}
        testId={IDS.loadError}
      />
    );
  }
  return (
    <StepScreenView
      screen={data}
      onBack={home}
      onNext={(nextId) => navigate(tenantHref(stepPath(pathwayId, nextId)))}
      onComplete={onComplete}
      onSubmitQuiz={onSubmitQuiz}
      onSubmitPractice={onSubmitPractice}
    />
  );
}

/**
 * `/parcours/:pathwayId/etape/:stepId` (contract §9). A step is a learner's page: signed
 * out, the page asks for the sign-in (the shared gate, `useRequireAuth`) and then shows the
 * step. The screen is keyed by the step, so "Next" starts a fresh screen (player, quiz and
 * completion state) and scrolls back to its top.
 */
export default function PathwayStep() {
  const t = useT("public");
  const { pathwayId, stepId } = useParams();
  const { user, loading, ensureAuthed, authDialog } = useRequireAuth();
  const rootRef = useRef(null);
  const shownRef = useRef(stepId);

  useEffect(() => {
    if (shownRef.current === stepId) return;
    shownRef.current = stepId;
    rootRef.current?.scrollIntoView?.({ block: "start" });
  }, [stepId]);

  let body;
  if (loading) {
    body = <Spinner />;
  } else if (!user) {
    body = (
      <div className="mx-auto max-w-xl rounded-3xl border border-eh-border-soft bg-eh-cream px-6 py-12 text-center">
        <h1 className="font-display text-3xl text-eh-ink">{t("pathways.step.signInTitle")}</h1>
        <p className="mt-3 text-eh-muted">{t("pathways.step.signInBody")}</p>
        <button
          type="button"
          onClick={() => ensureAuthed()}
          data-testid={IDS.notFoundSignIn}
          className="mt-6 inline-flex h-11 items-center rounded-full bg-eh-ink px-6 text-sm font-medium text-eh-cream"
        >
          {t("pathways.notFound.signIn")}
        </button>
      </div>
    );
  } else {
    body = <StepScreen key={`${pathwayId}/${stepId}`} pathwayId={pathwayId} stepId={stepId} />;
  }

  return (
    <ThemedPageLayout>
      <section ref={rootRef} className="scroll-mt-28 pt-32 pb-24 px-4 sm:px-6">{body}</section>
      {authDialog}
    </ThemedPageLayout>
  );
}
